import React, { useContext } from 'react';
import { Modal, ModalProps } from 'semantic-ui-react';
import styled, { createGlobalStyle } from 'styled-components';
import { SidebarContext } from 'common/contexts/SidebarContext';

const GlobalModalStyles = createGlobalStyle<{ $sidebarWidth: number; $isOpen: boolean }>`
  ${props => props.$isOpen && `
    /* Keep the overlay full-screen and above the sidebar. */
    .ui.page.modals.dimmer.transition.visible.active,
    .ui.dimmer.transition.visible.active {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100% !important;
      height: 100% !important;
      z-index: 11000 !important;
    }

    .ui.page.modals .ui.modal.transition.visible.active:not(.fullscreen),
    .ui.modal.transition.visible.active:not(.fullscreen) {
      position: fixed !important;
      top: 50% !important;
      left: 50% !important;
      right: auto !important;
      bottom: auto !important;
      margin: 0 !important;
      max-width: min(calc(100% - 40px), 1280px) !important;
      transform: translate(-50%, -50%) !important;
    }

    .ui.modal .close.icon,
    .ui.modal .close {
      color: #9CA3AF !important;
      opacity: 0.95 !important;
    }

    .ui.modal .close.icon:hover,
    .ui.modal .close:hover {
      color: #6B7280 !important;
      opacity: 1 !important;
    }

    .ui.page.modals .ui.fullscreen.modal.transition.visible.active,
    .ui.modal.transition.visible.active.fullscreen {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      margin: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      height: 100% !important;
      border-radius: 0 !important;
      z-index: 11001 !important;
    }
  `}
`;

interface PositionedModalProps extends ModalProps {
  children: React.ReactNode;
}

/**
 * A Modal component that automatically positions itself correctly 
 * relative to the sidebar, accounting for its expanded/collapsed state.
 * 
 * Use this instead of Semantic UI's Modal component directly when you need
 * proper positioning within the application pane.
 * 
 * @example
 * ```tsx
 * <PositionedModal open={isOpen} onClose={handleClose} size="small">
 *   <Modal.Header>My Modal</Modal.Header>
 *   <Modal.Content>Content here</Modal.Content>
 * </PositionedModal>
 * ```
 */
export const PositionedModal: React.FC<PositionedModalProps> = ({ 
  children, 
  open,
  ...modalProps 
}) => {
  const ctx = useContext(SidebarContext);
  const sidebarIsOpen = ctx?.isOpen ?? true; // default to open when context not present (e.g., in tests)
  const sidebarWidth = sidebarIsOpen ? 250 : 50;
  const { style, ...restModalProps } = modalProps;

  const modalStyle: React.CSSProperties | undefined = restModalProps.size === 'fullscreen'
    ? {
        ...(style as React.CSSProperties),
        position: 'fixed' as React.CSSProperties['position'],
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        margin: 0,
      }
    : style;

  return (
    <>
      {/* @ts-expect-error - styled-components GlobalStyleComponent type compatibility issue */}
      <GlobalModalStyles $sidebarWidth={sidebarWidth} $isOpen={open ?? false} />
      {/* Disable transition animations to avoid previous modal content lingering during rapid open/close cycles */}
      <Modal open={open} transition={{ duration: 0 }} style={modalStyle} {...restModalProps}>
        {children}
      </Modal>
    </>
  );
};

export default PositionedModal;
